// client/src/features/techs/techsApiSlice.js
import { apiSlice } from "../../app/api/apiSlice";

/**
 * NOTE:
 * - Ensure your base api has 'Tech' in tagTypes (apiSlice).
 *   e.g. tagTypes: ['User','Org','ATG','Tech']
 * - This assumes your server mounted routes at app.use('/techs', techsRoutes)
 *   so the base paths below are '/techs/...'.
 */
export const techsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    // GET /techs  -> { items: [], count }
    getTechs: builder.query({
      query: () => "/techs",
      transformResponse: (resp) => resp?.items ?? resp ?? [],
      providesTags: (result) =>
        result
          ? [
              { type: "Tech", id: "LIST" },
              ...result.map((t) => ({ type: "Tech", id: t.username || t.id })),
            ]
          : [{ type: "Tech", id: "LIST" }],
    }),

    // GET /techs/:username
    getTech: builder.query({
      query: (username) => `/techs/${encodeURIComponent(username)}`,
      providesTags: (result, error, arg) => [{ type: "Tech", id: arg }],
    }),

    // POST /techs
    // body: { username, name?, email, sendInvitation?, tempPassword?, permanentPassword? }
    createTech: builder.mutation({
      query: (body) => ({
        url: "/techs",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Tech", id: "LIST" }],
    }),

    // PATCH /techs/:username/password
    // body: { password }
    setTechPassword: builder.mutation({
      query: ({ username, password }) => ({
        url: `/techs/${encodeURIComponent(username)}/password`,
        method: "PATCH",
        body: { password },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Tech", id: arg.username }],
    }),

    // POST /techs/:username/enable
    enableTech: builder.mutation({
      query: (username) => ({
        url: `/techs/${encodeURIComponent(username)}/enable`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Tech", id: arg },
        { type: "Tech", id: "LIST" },
      ],
    }),

    // POST /techs/:username/disable
    disableTech: builder.mutation({
      query: (username) => ({
        url: `/techs/${encodeURIComponent(username)}/disable`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Tech", id: arg },
        { type: "Tech", id: "LIST" },
      ],
    }),

    // POST /techs/:username/mfa
    // body: { sms?: boolean, totp?: boolean }
    setTechMfa: builder.mutation({
      query: ({ username, sms = false, totp = false }) => ({
        url: `/techs/${encodeURIComponent(username)}/mfa`,
        method: "POST",
        body: { sms, totp },
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Tech", id: arg.username }],
    }),

    // DELETE /techs/:username
    deleteTech: builder.mutation({
      query: (username) => ({
        url: `/techs/${encodeURIComponent(username)}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Tech", id: "LIST" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTechsQuery,
  useGetTechQuery,
  useCreateTechMutation,
  useSetTechPasswordMutation,
  useEnableTechMutation,
  useDisableTechMutation,
  useSetTechMfaMutation,
  useDeleteTechMutation,
} = techsApiSlice;

export default techsApiSlice;
